import type { TableCopyMethod } from '@/analytics/enums/table.enums'
import { useMemo, type RefObject } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover/Popover'
import TableCopyPill from './TableCopyPill'

/** Anchor the range actions to the visible selection, including virtualized rows. */
export default function SelectedCellsPill({
  tableRef,
  count,
  onCopy,
  allowFootnotes = false,
  onClear,
}: {
  tableRef: RefObject<HTMLTableElement | null>
  allowFootnotes?: boolean
  count: number
  onCopy: (footnotes: boolean, method?: TableCopyMethod) => Promise<void>
  onClear: () => void
}) {
  const anchor = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () => {
          const table = tableRef.current
          const viewport = table?.parentElement?.getBoundingClientRect()
          if (!table || !viewport) return new DOMRect()
          const headerBottom = table.tHead?.getBoundingClientRect().bottom ?? viewport.top
          const cells = [...table.querySelectorAll<HTMLElement>('td[data-copy-selected="true"]')]
            .map((cell) => cell.getBoundingClientRect())
            .filter((bounds) => bounds.bottom > headerBottom && bounds.top < viewport.bottom && bounds.right > viewport.left && bounds.left < viewport.right)
          if (!cells.length) return new DOMRect(viewport.left, viewport.top - 1, 0, 0)
          const left = Math.max(viewport.left, Math.min(...cells.map((cell) => cell.left)))
          const right = Math.min(viewport.right, Math.max(...cells.map((cell) => cell.right)))
          const top = Math.max(headerBottom, Math.min(...cells.map((cell) => cell.top)))
          const bottom = Math.min(viewport.bottom, Math.max(...cells.map((cell) => cell.bottom)))
          return new DOMRect(left, top, right - left, bottom - top)
        },
      },
    }),
    [tableRef]
  )
  return (
    <Popover open>
      <PopoverAnchor virtualRef={anchor} />
      <PopoverContent
        aria-label="Selected cell actions"
        width="auto"
        showArrow={false}
        side="top"
        align="end"
        alignOffset={6}
        sideOffset={-6}
        collisionPadding={8}
        collisionBoundary={tableRef.current?.parentElement}
        sticky="always"
        hideWhenDetached
        updatePositionStrategy="always"
        className="rounded bg-white p-0 shadow-none ring-1 ring-inset ring-brand-neutral-300"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={() => {
          onClear()
          tableRef.current?.focus()
        }}
      >
        <TableCopyPill allowFootnotes={allowFootnotes} count={count} onCopy={onCopy} />
      </PopoverContent>
    </Popover>
  )
}
